import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import AppRouter from './AppRouter';
import './index.css';
import { AuthProvider } from './AuthContext';
import { LoaderProvider, useLoader } from './components/context/LoaderContext';
import { ThemeProvider } from './components/context/ThemeContext';
import Loader from './components/common/Loader';
import ClerkSessionBridge from './components/auth/ClerkSessionBridge';
import { ClerkProviderWithAppearance } from './components/auth/ClerkProviderWithAppearance';
import { initializeLoaderControl } from './services/api';
import { assertClerkConfigured } from './auth/clerkConfig';
import { isPublicAuthPath } from './auth/publicAuthPaths';
import { ROUTES } from './constants/routes';
import { installClerkFetchProbe } from './auth/authLifecycleDebug';
import AuthRouteProbe from './components/auth/AuthRouteProbe';

if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_AUTH_DEBUG === 'true') {
  installClerkFetchProbe();
}

const AppWithLoader = () => {
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    initializeLoaderControl({ showLoader, hideLoader });
  }, [showLoader, hideLoader]);

  useEffect(() => {
    const onAuthRequired = () => {
      if (!isPublicAuthPath(window.location.pathname)) {
        window.location.replace(ROUTES.LOGIN);
      }
    };
    window.addEventListener('intdoc:auth-required', onAuthRequired);
    return () => window.removeEventListener('intdoc:auth-required', onAuthRequired);
  }, []);

  return (
    <>
      <Loader />
      <AppRouter />
    </>
  );
};

function App() {
  let publishableKey;
  try {
    publishableKey = assertClerkConfigured();
  } catch (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-lg text-center">
          <h1 className="text-xl font-bold mb-2 primary-heading">Authentication not configured</h1>
          <p className="text-sm text-gray-600">{err.message}</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ThemeProvider>
        <ClerkProviderWithAppearance publishableKey={publishableKey}>
          <AuthRouteProbe />
          <ClerkSessionBridge />
          <AuthProvider>
            <LoaderProvider>
              <AppWithLoader />
            </LoaderProvider>
          </AuthProvider>
        </ClerkProviderWithAppearance>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
